import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { http, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Billing from "@/pages/Billing";
import { toast } from "sonner";
import { CalendarClock, CreditCard, ExternalLink, Loader2, ShieldCheck, AlertTriangle, Receipt } from "lucide-react";

const TONE = {
  TRIAL: "bg-sky-100 text-sky-700 border-sky-200",
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  ACTIVE_COMPLIMENTARY: "bg-emerald-100 text-emerald-700 border-emerald-200",
  TRIAL_EXPIRED: "bg-amber-100 text-amber-700 border-amber-200",
  PAST_DUE: "bg-orange-100 text-orange-700 border-orange-200",
  EXPIRED: "bg-red-100 text-red-700 border-red-200",
  SUSPENDED: "bg-slate-200 text-slate-700 border-slate-300",
};

export const fmtDate = (iso, lang) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(lang || undefined, { day: "2-digit", month: "long", year: "numeric" }); } catch { return iso.slice(0, 10); }
};

export function usePortal() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const r = await http.post("/billing/portal");
      window.location.href = r.data.portal_url;
    } catch (e) { toast.error(apiErr(e, t("sub.portal_error"))); setBusy(false); }
  };
  return { open, busy };
}

export default function SubscriptionTab({ admin }) {
  const { t, i18n } = useTranslation();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  const portal = usePortal();

  const load = () => http.get("/billing/subscription").then((r) => { setS(r.data); setErr(false); }).catch(() => setErr(true));
  useEffect(() => { load(); }, []);

  if (err) return <Card className="p-5 border-red-200 text-sm text-red-700 flex items-center justify-between" data-testid="sub-error">{t("sub.load_error")}<Button size="sm" variant="outline" onClick={load}>{t("common.retry", "Riprova")}</Button></Card>;
  if (!s) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const paying = s.status === "active";
  const canceling = paying && s.cancel_at_period_end;
  const price = s.plan_interval === "year" ? "600" : "60";
  const planLabel = s.plan_interval === "year" ? t("billing.annual") : s.plan_interval === "month" ? t("billing.monthly") : null;
  const needsPlan = ["TRIAL", "TRIAL_EXPIRED", "EXPIRED", "PAST_DUE"].includes(s.status) && !(paying);

  return (
    <div className="space-y-4" data-testid="subscription-tab">
      <Card className="p-5 border-slate-200 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{t("sub.current_state")}</div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span data-testid="sub-status-badge" className={`text-xs font-bold px-2.5 py-1 rounded-full border ${TONE[s.status] || TONE.SUSPENDED}`}>{t(`sub.state_${s.status}`, s.status)}</span>
              {canceling && <span data-testid="sub-canceling-badge" className="text-xs font-semibold px-2.5 py-1 rounded-full border bg-amber-50 text-amber-700 border-amber-200">{t("sub.state_canceling")}</span>}
            </div>
          </div>
          {(s.status === "TRIAL" || (paying && s.current_period_end)) && (
            <div className="text-right" data-testid="sub-days-left">
              <div className="text-3xl font-extrabold text-[#0F172A]">{s.days_remaining}</div>
              <div className="text-xs text-slate-500">{t("sub.days_left", { n: s.days_remaining })}</div>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <div className="rounded-xl bg-slate-50 p-3" data-testid="sub-plan">
            <div className="text-xs text-slate-500 flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> {t("sub.plan")}</div>
            <div className="font-semibold mt-0.5">{planLabel ? `${planLabel} · €${price}${s.plan_interval === "year" ? t("billing.per_year") : t("billing.per_month")}` : t("sub.no_plan")}</div>
          </div>
          <div className="rounded-xl bg-slate-50 p-3" data-testid="sub-period-end">
            <div className="text-xs text-slate-500 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> {s.status === "TRIAL" ? t("sub.trial_ends") : canceling ? t("sub.ends") : t("sub.renew")}</div>
            <div className="font-semibold mt-0.5">{fmtDate(s.status === "TRIAL" ? s.trial_ends_at : (s.current_period_end || s.comp_until), i18n.language)}</div>
          </div>
          <div className="rounded-xl bg-slate-50 p-3" data-testid="sub-source">
            <div className="text-xs text-slate-500 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t("sub.payment")}</div>
            <div className="font-semibold mt-0.5">{s.billing_source === "stripe" ? "Stripe" : s.billing_source === "bank" ? t("billing.pay_bank") : s.status === "ACTIVE_COMPLIMENTARY" ? t("sub.state_ACTIVE_COMPLIMENTARY") : "—"}</div>
          </div>
        </div>

        {s.status === "TRIAL" && <p className="text-sm text-slate-600" data-testid="sub-trial-hint">{t("sub.trial_hint")}</p>}
        {s.status === "PAST_DUE" && <p className="text-sm text-orange-700 flex items-center gap-1.5" data-testid="sub-past-due-hint"><AlertTriangle className="w-4 h-4" /> {t("sub.past_due_hint")}</p>}
        {canceling && <p className="text-sm text-amber-700" data-testid="sub-canceling-hint">{t("sub.canceling_hint", { date: fmtDate(s.current_period_end, i18n.language) })}</p>}
        {!s.stripe_enabled && <p className="text-xs text-slate-500" data-testid="sub-not-configured">{t("sub.not_configured")}</p>}

        {admin && s.portal_available && (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button data-testid="sub-portal-btn" onClick={portal.open} disabled={portal.busy} className="bg-[#0F172A] gap-1.5">
              {portal.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} {t("sub.manage")}
            </Button>
            <Button data-testid="sub-invoices-btn" variant="outline" onClick={portal.open} disabled={portal.busy} className="gap-1.5"><Receipt className="w-4 h-4" /> {t("sub.invoices")}</Button>
            <span className="text-xs text-slate-500 self-center">{t("sub.manage_hint")}</span>
          </div>
        )}
      </Card>

      {admin && needsPlan && (
        <div data-testid="sub-choose-plan">
          <h3 className="font-heading font-bold text-[#0F172A] mb-2">{t("sub.choose")}</h3>
          <Billing />
        </div>
      )}
      {!admin && <p className="text-xs text-slate-500" data-testid="sub-operator-hint">{t("sub.operator_hint")}</p>}
    </div>
  );
}
